import { useCallback, useState } from 'react'

import { useDebounce } from 'react-use'
import { getCharacterDetails } from '../api/character'

export const useCharacterDetailsStore = <T>(
  getEntityId: (entity: T) => number | undefined,
) => {
  const [detailsStore, setDetailsStore] = useState<{
    [id: string]: wowaudit.CharacterDetails
  }>({})
  const [pendingRecords, setPendingRecords] = useState<T[]>([])

  const fetchPendingRecords = useCallback(
    async (newRecords?: T[], force = false) => {
      const recordsToFetch = newRecords ?? pendingRecords
      if (recordsToFetch.length === 0) return

      const missingIds = recordsToFetch
        .map((entity) => getEntityId(entity))
        .filter((id) => id && (!detailsStore[id] || force)) as number[]

      if (missingIds.length === 0) return

      const newDetails = await getCharacterDetails(missingIds)

      const newDetailsMap = newDetails.reduce((acc, details) => {
        acc[details.id] = details
        return acc
      }, {} as Record<string, wowaudit.CharacterDetails>)

      setDetailsStore((prev) => ({ ...prev, ...newDetailsMap }))
    },
    [pendingRecords, detailsStore, getEntityId],
  )

  useDebounce(fetchPendingRecords, 300, [
    pendingRecords,
    detailsStore,
    getEntityId,
  ])

  const fetchNewDetails = (newRecords: T[]) => {
    setPendingRecords(newRecords)
  }

  const immediatelyFetchNewDetails = (newRecords: T[], force = false) => {
    fetchPendingRecords(newRecords, force)
  }

  return {
    detailsStore,
    fetchNewDetails,
    immediatelyFetchNewDetails,
  }
}
